export type Locale = 'en' | 'it';

export const messages = {
  en: {
    nav: {
      home: 'Today',
      roadmap: 'Roadmap',
      ai: 'Orivon AI',
      community: 'Community',
      network: 'Network',
      messages: 'Messages',
      opportunities: 'Opportunities',
      notifications: 'Notifications',
      settings: 'Settings',
      admin: 'Admin'
    },
    common: {
      loading: 'Loading your Orivon workspace…',
      retry: 'Try again',
      save: 'Save',
      cancel: 'Cancel',
      signOut: 'Sign out',
      free: 'Free',
      professional: 'Professional',
      viewRoadmap: 'View roadmap',
      completeAssessment: 'Complete assessment',
      appName: 'Professional operating system'
    },
    auth: {
      title: 'Continue your Orivon path',
      subtitle: 'Sign in to access your saved profile, roadmap and progress.',
      signupTitle: 'Create your free Orivon profile',
      signupSubtitle: 'Your direction, category and roadmap stay saved. Choose a password and your profile is ready on every device.',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign in',
      create: 'Create account',
      forgot: 'Send password reset link',
      switchCreate: 'New to Orivon? Create account',
      switchSignIn: 'Already registered? Sign in',
      confirmation: 'Check your email to confirm your account.',
      resetSent: 'Password reset link sent.',
      unavailable: 'The account service is temporarily unavailable.',
      invalid: 'Enter a valid email and a password of at least 8 characters.'
    },
    dashboard: {
      eyebrow: 'Your Career Proof path',
      title: 'Turn uncertainty into one piece of evidence at a time.',
      subtitle: 'One direction, one current mission and the signals you need for a better career decision.',
      currentLevel: 'Current level',
      weeklyFocus: 'Weekly focus',
      nextMission: 'Next mission',
      progress: 'Roadmap progress',
      network: 'Network activity',
      opportunities: 'Relevant opportunities',
      noAssessment: 'Your assessment is not linked yet.',
      noAssessmentBody: 'Complete the Orivon analysis to generate your category and starting direction.',
      focusFallback: 'Define one measurable proof of ability in your current direction.',
      missionFallback: 'Choose a small real-world output you can complete this week.',
      emptyNetwork: 'Complete your profile to become discoverable in your category.',
      emptyOpportunities: 'Relevant, curated opportunities will appear here when available.'
    },
    roadmap: {
      eyebrow: 'Your Orivon path',
      title: 'See the whole direction. Work on the stage that matters now.',
      subtitle: 'Your roadmap turns long-term potential into skills, evidence and focused weekly progress.',
      loadError: 'Your roadmap could not be loaded.',
      updateError: 'Your progress could not be updated.',
      choosePath: 'Choose your operating path',
      choosePathBody: 'The destination remains visible while priorities adapt to your current constraints.',
      paths: {
        fast: 'Fast',
        balanced: 'Balanced',
        ambitious: 'Ambitious'
      },
      priorities: {
        title: 'Adjust your priorities',
        subtitle: 'Your potential does not change. What changes is which path fits the conditions you choose.',
        study: 'Study availability',
        capital: 'Capital available',
        income: 'Earliest validation window',
        preference: 'Preference',
        risk: 'Risk tolerance',
        studyOptions: { none: 'None', short: 'Up to 6 months', medium: '6 months to 2 years', long: 'More than 2 years' },
        capitalOptions: { none: 'Less than $1,000', low: '$1,000 – $10,000', medium: '$10,000 – $50,000', high: 'More than $50,000' },
        incomeOptions: { now: 'As soon as possible', '3mo': 'Within 3 months', '12mo': 'Within 12 months', wait: 'No rush' },
        preferenceOptions: { employed: 'Employed role', independent: 'Independent path', both: 'Both' },
        riskOptions: { low: 'Low', medium: 'Medium', high: 'High' },
        recommended: 'Recommended path',
        sectionEyebrow: 'Career paths for you',
        compatibility: 'Compatibility',
        feasibility: 'Feasibility for you',
        duration: 'Duration',
        firstValidation: 'First validation',
        capitalLabel: 'Capital',
        steps: 'Steps',
        cardTitles: { ideal: 'Your Ideal Destination', accessible: 'Your Accessible Career', independent: 'Your Independent Path' },
        cardSubtitles: {
          ideal: 'The path that expresses your potential to the fullest.',
          accessible: 'The best career you can start in your current conditions.',
          independent: 'The entrepreneurial route most coherent with your skills, capital and risk appetite.'
        },
        destination: 'Destination',
        choose: 'Choose this path',
        chosen: '✓ Chosen',
        paceVsDestination: 'This controls the pace of the levels below. The destination you aim for is chosen separately, in the cards further down.',
        destinationAppliedNote: 'Updated to match the destination you chose below.'
      },
      overallProgress: 'Overall progress',
      skillsCompleted: '{done} of {total} skills completed',
      level: 'Level',
      longTerm: 'Long-term direction',
      whyNow: 'Why this level: ',
      thisWeek: 'This week',
      skillFocus: 'Skill focus',
      skillsTitle: 'Build proof, not only knowledge',
      milestones: 'Milestones',
      proveProgress: 'Prove the level before advancing',
      monthlyReview: 'Monthly review',
      reviewTitle: 'Review what changed',
      reviewBody: 'Record outcomes, blockers and the priority that should shape your next cycle.',
      reviewSaved: 'Review saved',
      openReview: 'Open monthly review',
      saveDraft: 'Save draft',
      completeReview: 'Complete review',
      reviewFields: {
        outcomes: 'Outcomes achieved',
        blockers: 'Main blockers',
        lessons: 'What you learned',
        nextPriority: 'Next priority'
      },
      current: 'Current',
      locked: 'Professional level locked',
      completed: 'Completed',
      completeMission: 'Complete mission',
      startSkill: 'Start',
      completeSkill: 'Complete',
      verifyMilestone: 'Verify milestone',
      proofPrompt: 'Describe the evidence that proves you completed this skill.',
      missionProofPrompt: 'What did you produce, validate or learn from this mission?',
      milestonePrompt: 'Summarize the evidence that proves this level is complete.',
      freeLevelBody: 'This is the realistic point you can begin validating now. Professional unlocks the intermediate sequence.',
      upgradeTitle: 'Do not bet on a direction before you test it.',
      upgradeBody: 'Unlock the prioritized direction, four personalized missions and a decision checkpoint at the end of your 30-day Sprint.',
      upgradeCta: 'Build my 30-day plan',
      states: {
        locked: 'Locked',
        upcoming: 'Upcoming',
        available: 'Available',
        active: 'Active',
        current: 'Current',
        in_progress: 'In progress',
        completed: 'Completed',
        verified: 'Verified'
      }
    },
    ai: {
      title: 'A personal AI that knows your path',
      subtitle: 'Ask about your current focus, evidence, opportunities and decisions using your real Orivon context.',
      loadError: 'Orivon AI could not be loaded.',
      sendError: 'Orivon AI could not answer. Your message was not lost.',
      actionError: 'The proposed action could not be updated.',
      proTitle: 'Turn your profile and roadmap into a continuous decision system.',
      proBody: 'Professional gives Orivon AI access to your roadmap, progress and selected memory—without exposing it to other members.',
      proCta: 'Unlock Orivon Professional',
      conversations: 'Conversations',
      newThread: 'New conversation',
      noSummary: 'No summary yet',
      noThreads: 'Start a conversation when you are ready.',
      monthlyUsage: 'Monthly AI usage',
      contextualAssistant: 'Contextual assistant',
      memory: 'Memory',
      delete: 'Delete',
      deleteConfirm: 'Permanently delete this conversation?',
      notConfiguredTitle: 'Orivon AI needs a server credential.',
      notConfiguredBody: 'No answer is simulated. Configure AI_PROVIDER, AI_API_KEY and AI_MODEL in Netlify to activate real responses.',
      welcomeTitle: 'What should move forward next?',
      welcomeBody: 'Orivon AI can read your current roadmap and progress. It proposes changes first; nothing is applied without your confirmation.',
      prompts: [
        'Build a realistic focus for this week.',
        'Which skill is currently blocking my progress?',
        'Am I ready for the next level?',
        'Review this month and identify my next priority.'
      ],
      you: 'You',
      proposedAction: 'Proposed change',
      reject: 'Reject',
      confirm: 'Review and confirm',
      reviewActionTitle: 'Review the proposed change',
      reviewActionBody: 'Check exactly what Orivon will update. Nothing changes until you confirm.',
      applyChange: 'Confirm and apply',
      deleteDialogTitle: 'Delete this conversation?',
      clearMemoryDialogTitle: 'Clear Orivon AI memory?',
      confirmDestructive: 'Delete permanently',
      actionStates: {
        proposed: 'Waiting for your confirmation',
        confirmed: 'Confirmed',
        applied: 'Applied',
        rejected: 'Rejected',
        failed: 'Could not be applied'
      },
      placeholder: 'Ask about your path, a decision or your next action…',
      send: 'Send',
      disclaimer: 'Orivon provides guidance, not medical, legal, financial or income guarantees.',
      limitReached: 'You have used the available AI requests for this period.',
      minutes: 'minutes',
      paths: {
        fast: 'Fast path',
        balanced: 'Balanced path',
        ambitious: 'Ambitious path'
      },
      actionFields: {
        focus: 'Weekly focus',
        obstacle: 'Obstacle',
        skill: 'Blocking skill',
        mission: 'Mission',
        description: 'What you will do',
        criteria: 'Completion evidence',
        time: 'Estimated time',
        path: 'Roadmap path',
        outcomes: 'Outcomes',
        blockers: 'Blockers',
        lessons: 'Lessons',
        priority: 'Next priority',
        evidence: 'Evidence'
      },
      errors: {
        ai_not_configured: 'Orivon AI is not configured on the server yet.',
        provider_timeout: 'The response is taking too long. Please try again shortly.',
        provider_rate_limited: 'The AI service is temporarily busy. Please try again shortly.',
        provider_unavailable: 'Orivon AI is temporarily unavailable. Please try again shortly.',
        provider_refusal: 'This request cannot be completed. Try reframing it as a professional question.',
        provider_invalid_output: 'The response did not pass quality checks. Please try again.',
        provider_auth_failed: 'The AI configuration requires technical attention.',
        ai_daily_limit: 'You reached today’s Orivon AI limit. You can continue tomorrow.',
        ai_monthly_limit: 'You reached this month’s Orivon AI limit.',
        ai_request_failed: 'The AI request could not be completed.'
      },
      memoryBody: 'Memory stores useful decisions and preferences from your conversations. You control it.',
      memoryActive: 'Use memory in this conversation',
      memoryTypes: {
        fact: 'Fact',
        decision: 'Decision',
        preference: 'Preference',
        plan: 'Plan'
      },
      remove: 'Remove',
      noMemory: 'No saved memory yet.',
      clearMemory: 'Clear all memory',
      clearMemoryConfirm: 'Permanently clear all Orivon AI memory?'
    },
    social: {
      feedTitle: 'Professional conversations',
      feedBody: 'Share evidence, decisions and useful questions with real Orivon members.',
      general: 'General feed',
      category: 'Category feed',
      createPlaceholder: 'Share a useful observation, question or piece of progress…',
      linkPlaceholder: 'Optional link (https://…)',
      publish: 'Publish',
      publishing: 'Publishing…',
      postLimit: 'Maximum 2,000 characters.',
      member: 'Private member',
      edited: 'Edited',
      useful: 'Useful',
      comments: 'Comments',
      save: 'Save',
      saved: 'Saved',
      more: 'More actions',
      edit: 'Edit',
      delete: 'Delete',
      report: 'Report',
      reportReason: 'Why are you reporting this?',
      reportSent: 'Report sent for review.',
      deleteConfirm: 'Delete this content?',
      editPrompt: 'Edit your content',
      commentPlaceholder: 'Write a considered response…',
      replyPlaceholder: 'Write a reply…',
      reply: 'Reply',
      submitComment: 'Comment',
      noPosts: 'No posts here yet.',
      noPostsBody: 'Start a real professional conversation. Orivon never invents community activity.',
      loadError: 'The community feed could not be loaded.',
      actionError: 'This community action could not be completed.',
      reasons: {
        spam: 'Spam',
        harassment: 'Harassment',
        impersonation: 'Impersonation',
        scam: 'Scam',
        inappropriate: 'Inappropriate content',
        privacy: 'Privacy concern',
        other: 'Other'
      }
    },
    network: {
      title: 'Build a useful professional network.',
      subtitle: 'Orivon explains why a person may be relevant without pretending compatibility is certainty.',
      suggestions: 'Suggested people',
      connections: 'Connections',
      requests: 'Requests',
      networkFeed: 'Connection feed',
      filters: 'Matching filters',
      allCategories: 'All categories',
      sector: 'Sector',
      location: 'City or country',
      language: 'Language',
      apply: 'Apply filters',
      clear: 'Clear',
      connect: 'Connect',
      requestTitle: 'Send a connection request',
      requestTo: 'Introduce yourself to {name}. A short, relevant note makes the request more useful.',
      messageOptional: 'Optional introduction',
      messagePrompt: 'Add an optional, relevant introduction (maximum 500 characters).',
      characters: '{count}/500 characters',
      sendRequest: 'Send request',
      requestSent: 'Request sent',
      incoming: 'Incoming',
      outgoing: 'Sent',
      accept: 'Accept',
      decline: 'Decline',
      cancelRequest: 'Cancel request',
      removeConnection: 'Remove connection',
      removeConfirm: 'Remove this connection?',
      block: 'Block',
      blockConfirm: 'Block this member? Connections and pending requests will be removed.',
      emptySuggestions: 'No explainable matches are available yet.',
      emptySuggestionsBody: 'Complete your profile and privacy choices. Suggestions appear only when real, discoverable members share enough relevant data.',
      emptyConnections: 'No connections yet.',
      emptyRequests: 'No pending requests.',
      reasonsTitle: 'Why this match',
      missingTitle: 'Missing context',
      profile: 'View profile',
      freeLimit: 'Free shows up to 5 explainable suggestions.',
      proLimit: 'Professional shows up to 20 explainable suggestions with advanced filters.',
      loadError: 'Your network could not be loaded.',
      actionError: 'The network action could not be completed.',
      privateMember: 'Private member',
      labels: {
        high: 'Strong signal',
        medium: 'Relevant signal',
        emerging: 'Early signal'
      },
      types: {
        similar: 'Similar direction',
        complementary: 'Complementary',
        more_advanced: 'Further on the path',
        same_territory: 'Same territory',
        relevant: 'Potentially relevant'
      },
      reasons: {
        shared_category: 'Shared Orivon category',
        shared_sector: 'Shared sector',
        same_city: 'Same city',
        shared_language: 'Shared language',
        offers_what_you_seek: 'Offers something you are seeking',
        seeks_what_you_offer: 'Seeks something you can offer',
        further_on_path: 'Further ahead on a related path'
      }
    },
    messaging: {
      title: 'Private professional conversations',
      subtitle: 'Messages are available only between accepted connections. Email and phone numbers stay private.',
      conversations: 'Conversations',
      privateMember: 'Private connection',
      noConversations: 'No conversations yet.',
      noConversationsBody: 'Connect with a member before starting a private conversation.',
      chooseConversation: 'Choose a conversation.',
      placeholder: 'Write a message…',
      send: 'Send',
      deleted: 'Message deleted',
      delete: 'Delete',
      deleteConfirm: 'Delete this message for both participants?',
      report: 'Report',
      reportConfirm: 'Report this message for inappropriate content?',
      reportSent: 'Message reported.',
      block: 'Block member',
      blockConfirm: 'Block this member and end messaging access?',
      loadError: 'Messages could not be loaded.',
      actionError: 'The message action could not be completed.',
      unread: 'unread'
    },
    notifications: {
      title: 'Notifications',
      subtitle: 'Connection, community, message and progress updates from your real activity.',
      markAll: 'Mark all as read',
      empty: 'No notifications yet.',
      emptyBody: 'Updates will appear when real activity involves your account.',
      loadError: 'Notifications could not be loaded.',
      types: {
        connection_request: 'New connection request',
        connection_accepted: 'Connection accepted',
        comment: 'New comment on your post',
        reply: 'New reply',
        reaction: 'New reaction to your post',
        message: 'New private message',
        mission: 'Mission progress updated',
        milestone: 'Milestone completed',
        review: 'Monthly review updated',
        ai_suggestion: 'New Orivon AI suggestion',
        opportunity: 'New relevant opportunity'
      }
    },
    opportunities: {
      title: 'Curated paths into real work.',
      subtitle: 'Explore roles, collaborations, programs and tools selected by Orivon. Compatibility is explained, never presented as certainty.',
      curated: 'Human-curated',
      type: 'Opportunity type',
      allTypes: 'All opportunity types',
      sector: 'Sector',
      remoteOnly: 'Remote only',
      savedOnly: 'Saved only',
      apply: 'Apply filters',
      clear: 'Clear',
      organic: 'Orivon selection',
      sponsored: 'Sponsored',
      remote: 'Remote',
      deadline: 'Deadline',
      why: 'Why it may be relevant',
      missing: 'Complete your profile for better context',
      exploratoryBody: 'This is an exploratory opportunity. Orivon does not yet have enough profile data for a stronger explanation.',
      sponsoredDisclosure: 'Paid placement by {sponsor}. Sponsorship never changes your assessment or career direction.',
      open: 'Open opportunity',
      save: 'Save',
      saved: 'Saved',
      askAi: 'Assess with Orivon AI',
      empty: 'No suitable opportunities are published yet.',
      emptyBody: 'Orivon shows only real, reviewed opportunities. It never invents roles, programs or activity to fill this page.',
      noSaved: 'You have not saved any opportunities.',
      noSavedBody: 'Save a relevant opportunity to keep it here for later.',
      loadError: 'Opportunities could not be loaded.',
      actionError: 'The opportunity action could not be completed.',
      labels: {
        strong: 'Strong signal',
        relevant: 'Relevant signal',
        exploratory: 'Exploratory'
      },
      types: {
        role: 'Role',
        collaboration: 'Collaboration',
        program: 'Program',
        event: 'Event',
        course: 'Course',
        accelerator: 'Accelerator',
        tool: 'Tool'
      },
      reasons: {
        category_alignment: 'Aligned with your Orivon category',
        sector_alignment: 'Aligned with your sector',
        skill_alignment: 'Relevant to strengths in your profile',
        remote_access: 'Accessible remotely',
        roadmap_stage_alignment: 'Compatible with your current roadmap stage'
      }
    },
    admin: {
      staffWorkspace: 'Staff workspace',
      title: 'Moderate carefully. Curate deliberately.',
      subtitle: 'Reports, opportunity publishing and enforcement actions are protected and recorded in an immutable audit trail.',
      unauthorized: 'Staff access required',
      unauthorizedBody: 'This route is available only to authorized Orivon moderators and administrators.',
      openReports: 'Open reports',
      publishedOpportunities: 'Published opportunities',
      activeSuspensions: 'Active suspensions',
      tabs: {
        reports: 'Reports',
        opportunities: 'Opportunities',
        suspensions: 'Suspensions',
        activity: 'Activity log'
      },
      noDetails: 'No additional details.',
      noReports: 'No reports need review.',
      noOpportunities: 'No opportunities have been curated yet.',
      noSuspensions: 'No active suspensions.',
      noActivity: 'No moderation actions yet.',
      moderationNote: 'Add an internal moderation note.',
      actionDialogBody: 'Review the target and confirm this audited staff action.',
      resolveReportTitle: 'Resolve report',
      dismissReportTitle: 'Dismiss report',
      hideContentTitle: 'Hide reported content',
      suspendDialogTitle: 'Suspend community access',
      unsuspendDialogTitle: 'Remove community suspension',
      editCategoryTitle: 'Edit category',
      confirmAction: 'Confirm action',
      hideContent: 'Hide content',
      dismiss: 'Dismiss',
      resolve: 'Resolve',
      edit: 'Edit',
      newOpportunity: 'New opportunity',
      editOpportunity: 'Edit opportunity',
      curation: 'Opportunity curation',
      titleEn: 'English title',
      titleIt: 'Italian title',
      descriptionEn: 'English description',
      descriptionIt: 'Italian description',
      type: 'Type',
      sector: 'Sector',
      location: 'Location',
      source: 'Source label',
      placement: 'Placement',
      sponsor: 'Sponsor name',
      categories: 'Category targets (comma separated)',
      skills: 'Skill targets (comma separated)',
      deadline: 'Deadline',
      status: 'Status',
      remote: 'Remote',
      until: 'Until',
      indefinite: 'Indefinite',
      unsuspend: 'Remove suspension',
      unsuspendConfirm: 'Remove this community suspension?',
      unsuspendedByStaff: 'Suspension removed after staff review.',
      suspensionReason: 'Why is this member being suspended from community actions?',
      suspensionReasonRequired: 'Enter a suspension reason of at least three characters.',
      suspendMember: 'Suspend community access',
      memberId: 'Member user ID',
      suspend: 'Suspend',
      enforcement: 'Enforcement',
      taxonomy: 'Taxonomy',
      categoriesTitle: 'Orivon categories',
      categoryNameEn: 'English category name',
      categoryNameIt: 'Italian category name',
      categoryDescriptionEn: 'English category description',
      categoryDescriptionIt: 'Italian category description',
      saved: 'Staff change saved and audited.',
      actionError: 'The staff action could not be completed.'
    },
    modules: {
      roadmapTitle: 'Your professional roadmap',
      roadmapBody: 'See your current level, long-term direction and verified progress.',
      aiTitle: 'Orivon AI',
      aiBody: 'A contextual assistant for your profile, roadmap and decisions.',
      communityTitle: 'Community',
      communityBody: 'Category, network and general professional conversations.',
      networkTitle: 'Your network',
      networkBody: 'People with similar goals, complementary skills or relevant experience.',
      messagesTitle: 'Messages',
      messagesBody: 'Private conversations with eligible connections.',
      opportunitiesTitle: 'Opportunities',
      opportunitiesBody: 'Curated roles, collaborations, programs and resources.',
      notificationsTitle: 'Notifications',
      notificationsBody: 'Connection, community, mission and message updates.',
      settingsTitle: 'Settings',
      settingsBody: 'Account, profile, privacy, language, notifications and billing.',
      foundation: 'Launch V1 module foundation is active. Data workflows are implemented milestone by milestone on this preview branch.'
    }
  },
  it: {
    nav: {
      home: 'Oggi',
      roadmap: 'Percorso',
      ai: 'Orivon AI',
      community: 'Community',
      network: 'Rete',
      messages: 'Messaggi',
      opportunities: 'Opportunità',
      notifications: 'Notifiche',
      settings: 'Impostazioni',
      admin: 'Admin'
    },
    common: {
      loading: 'Caricamento del tuo spazio Orivon…',
      retry: 'Riprova',
      save: 'Salva',
      cancel: 'Annulla',
      signOut: 'Esci',
      free: 'Free',
      professional: 'Professional',
      viewRoadmap: 'Apri il percorso',
      completeAssessment: 'Completa l’analisi',
      appName: 'Sistema operativo professionale'
    },
    auth: {
      title: 'Continua il tuo percorso Orivon',
      subtitle: 'Accedi per ritrovare profilo, percorso e progressi.',
      signupTitle: 'Crea il tuo profilo Orivon gratuito',
      signupSubtitle: 'Direzione, categoria e percorso restano salvati. Scegli una password e il tuo profilo è pronto su ogni dispositivo.',
      email: 'Email',
      password: 'Password',
      signIn: 'Accedi',
      create: 'Crea account',
      forgot: 'Invia link per reimpostare la password',
      switchCreate: 'Nuovo su Orivon? Crea account',
      switchSignIn: 'Hai già un account? Accedi',
      confirmation: 'Controlla la tua email per confermare l’account.',
      resetSent: 'Link per reimpostare la password inviato.',
      unavailable: 'Il servizio account è temporaneamente non disponibile.',
      invalid: 'Inserisci un’email valida e una password di almeno 8 caratteri.'
    },
    dashboard: {
      eyebrow: 'Il tuo percorso Career Proof',
      title: 'Trasforma l’incertezza in una prova alla volta.',
      subtitle: 'Una direzione, una missione attuale e i segnali che servono per una decisione professionale migliore.',
      currentLevel: 'Livello attuale',
      weeklyFocus: 'Focus settimanale',
      nextMission: 'Prossima missione',
      progress: 'Progresso percorso',
      network: 'Attività della rete',
      opportunities: 'Opportunità rilevanti',
      noAssessment: 'La tua analisi non è ancora collegata.',
      noAssessmentBody: 'Completa l’analisi Orivon per generare categoria e direzione di partenza.',
      focusFallback: 'Definisci una prova misurabile delle tue capacità nella direzione attuale.',
      missionFallback: 'Scegli un piccolo risultato reale che puoi completare questa settimana.',
      emptyNetwork: 'Completa il profilo per diventare visibile nella tua categoria.',
      emptyOpportunities: 'Le opportunità curate e rilevanti appariranno qui quando disponibili.'
    },
    roadmap: {
      eyebrow: 'Il tuo percorso Orivon',
      title: 'Guarda l’intera direzione. Lavora sulla fase che conta adesso.',
      subtitle: 'Il percorso trasforma il potenziale di lungo periodo in competenze, prove e progressi settimanali mirati.',
      loadError: 'Non è stato possibile caricare il percorso.',
      updateError: 'Non è stato possibile aggiornare i progressi.',
      choosePath: 'Scegli il percorso operativo',
      choosePathBody: 'La destinazione resta visibile mentre le priorità si adattano ai tuoi vincoli attuali.',
      paths: {
        fast: 'Rapido',
        balanced: 'Equilibrato',
        ambitious: 'Ambizioso'
      },
      priorities: {
        title: 'Modifica le tue priorità',
        subtitle: 'Il tuo potenziale non cambia. Cambia quale percorso si adatta alle condizioni che scegli.',
        study: 'Disponibilità allo studio',
        capital: 'Capitale disponibile',
        income: 'Prima finestra di validazione',
        preference: 'Preferenza',
        risk: 'Propensione al rischio',
        studyOptions: { none: 'Nessuna', short: 'Fino a 6 mesi', medium: 'Da 6 mesi a 2 anni', long: 'Più di 2 anni' },
        capitalOptions: { none: 'Meno di 1.000 €', low: '1.000 – 10.000 €', medium: '10.000 – 50.000 €', high: 'Più di 50.000 €' },
        incomeOptions: { now: 'Il prima possibile', '3mo': 'Entro 3 mesi', '12mo': 'Entro 12 mesi', wait: 'Nessuna fretta' },
        preferenceOptions: { employed: 'Ruolo dipendente', independent: 'Percorso indipendente', both: 'Entrambi' },
        riskOptions: { low: 'Bassa', medium: 'Media', high: 'Alta' },
        recommended: 'Percorso consigliato',
        sectionEyebrow: 'Percorsi di carriera per te',
        compatibility: 'Compatibilità',
        feasibility: 'Fattibilità per te',
        duration: 'Durata',
        firstValidation: 'Prima validazione',
        capitalLabel: 'Capitale',
        steps: 'Passaggi',
        cardTitles: { ideal: 'La Tua Destinazione Ideale', accessible: 'La Tua Carriera Accessibile', independent: 'La Tua Strada Indipendente' },
        cardSubtitles: {
          ideal: 'Il percorso che esprime al massimo il tuo potenziale.',
          accessible: 'La migliore carriera che puoi iniziare nelle tue condizioni attuali.',
          independent: 'Il percorso imprenditoriale più coerente con capacità, capitale e propensione al rischio.'
        },
        destination: 'Destinazione',
        choose: 'Scegli questo percorso',
        chosen: '✓ Scelto',
        paceVsDestination: 'Questo controlla il ritmo dei livelli qui sotto. La destinazione a cui punti si sceglie separatamente, nelle card più in basso.',
        destinationAppliedNote: 'Aggiornata in base alla destinazione che hai scelto qui sotto.'
      },
      overallProgress: 'Progresso complessivo',
      skillsCompleted: '{done} competenze completate su {total}',
      level: 'Livello',
      longTerm: 'Direzione di lungo periodo',
      whyNow: 'Perché questo livello: ',
      thisWeek: 'Questa settimana',
      skillFocus: 'Focus competenze',
      skillsTitle: 'Costruisci prove, non solo conoscenza',
      milestones: 'Milestone',
      proveProgress: 'Dimostra il livello prima di avanzare',
      monthlyReview: 'Revisione mensile',
      reviewTitle: 'Rivedi ciò che è cambiato',
      reviewBody: 'Registra risultati, ostacoli e la priorità che deve guidare il prossimo ciclo.',
      reviewSaved: 'Revisione salvata',
      openReview: 'Apri la revisione mensile',
      saveDraft: 'Salva bozza',
      completeReview: 'Completa revisione',
      reviewFields: {
        outcomes: 'Risultati ottenuti',
        blockers: 'Ostacoli principali',
        lessons: 'Cosa hai imparato',
        nextPriority: 'Prossima priorità'
      },
      current: 'Attuale',
      locked: 'Livello Professional bloccato',
      completed: 'Completata',
      completeMission: 'Completa missione',
      startSkill: 'Inizia',
      completeSkill: 'Completa',
      verifyMilestone: 'Verifica milestone',
      proofPrompt: 'Descrivi la prova che dimostra il completamento di questa competenza.',
      missionProofPrompt: 'Cosa hai prodotto, validato o imparato con questa missione?',
      milestonePrompt: 'Riassumi le prove che dimostrano il completamento di questo livello.',
      freeLevelBody: 'Questo è il punto realistico che puoi iniziare a validare oggi. Professional sblocca la sequenza intermedia.',
      upgradeTitle: 'Non scommettere su una direzione prima di averla testata.',
      upgradeBody: 'Sblocca la direzione prioritaria, quattro missioni personalizzate e un checkpoint decisionale alla fine del tuo Sprint di 30 giorni.',
      upgradeCta: 'Costruisci il mio piano di 30 giorni',
      states: {
        locked: 'Bloccata',
        upcoming: 'Futura',
        available: 'Disponibile',
        active: 'Attiva',
        current: 'Attuale',
        in_progress: 'In corso',
        completed: 'Completata',
        verified: 'Verificata'
      }
    },
    ai: {
      title: 'Un’AI personale che conosce il tuo percorso',
      subtitle: 'Fai domande su focus, prove, opportunità e decisioni usando il tuo vero contesto Orivon.',
      loadError: 'Non è stato possibile caricare Orivon AI.',
      sendError: 'Orivon AI non ha potuto rispondere. Il messaggio non è andato perso.',
      actionError: 'Non è stato possibile aggiornare l’azione proposta.',
      proTitle: 'Trasforma profilo e percorso in un sistema decisionale continuo.',
      proBody: 'Professional permette a Orivon AI di usare percorso, progressi e memoria scelta, senza esporli agli altri membri.',
      proCta: 'Sblocca Orivon Professional',
      conversations: 'Conversazioni',
      newThread: 'Nuova conversazione',
      noSummary: 'Nessun riepilogo',
      noThreads: 'Inizia una conversazione quando vuoi.',
      monthlyUsage: 'Utilizzo AI mensile',
      contextualAssistant: 'Assistente contestuale',
      memory: 'Memoria',
      delete: 'Elimina',
      deleteConfirm: 'Eliminare definitivamente questa conversazione?',
      notConfiguredTitle: 'Orivon AI richiede una credenziale server.',
      notConfiguredBody: 'Non vengono simulate risposte. Configura AI_PROVIDER, AI_API_KEY e AI_MODEL su Netlify per attivare risposte reali.',
      welcomeTitle: 'Cosa deve avanzare adesso?',
      welcomeBody: 'Orivon AI può leggere percorso e progressi. Prima propone le modifiche: nulla viene applicato senza conferma.',
      prompts: [
        'Costruisci un focus realistico per questa settimana.',
        'Quale competenza sta bloccando i miei progressi?',
        'Sono pronto per il prossimo livello?',
        'Analizza questo mese e identifica la mia prossima priorità.'
      ],
      you: 'Tu',
      proposedAction: 'Modifica proposta',
      reject: 'Rifiuta',
      confirm: 'Controlla e conferma',
      reviewActionTitle: 'Controlla la modifica proposta',
      reviewActionBody: 'Verifica esattamente cosa aggiornerà Orivon. Nulla cambia finché non confermi.',
      applyChange: 'Conferma e applica',
      deleteDialogTitle: 'Eliminare questa conversazione?',
      clearMemoryDialogTitle: 'Cancellare la memoria di Orivon AI?',
      confirmDestructive: 'Elimina definitivamente',
      actionStates: {
        proposed: 'In attesa della tua conferma',
        confirmed: 'Confermata',
        applied: 'Applicata',
        rejected: 'Rifiutata',
        failed: 'Non è stato possibile applicarla'
      },
      placeholder: 'Chiedi del tuo percorso, di una decisione o della prossima azione…',
      send: 'Invia',
      disclaimer: 'Orivon offre indicazioni, non garanzie mediche, legali, finanziarie o di reddito.',
      limitReached: 'Hai utilizzato le richieste AI disponibili per questo periodo.',
      minutes: 'minuti',
      paths: {
        fast: 'Percorso rapido',
        balanced: 'Percorso equilibrato',
        ambitious: 'Percorso ambizioso'
      },
      actionFields: {
        focus: 'Focus settimanale',
        obstacle: 'Ostacolo',
        skill: 'Competenza bloccante',
        mission: 'Missione',
        description: 'Cosa farai',
        criteria: 'Prova di completamento',
        time: 'Tempo stimato',
        path: 'Tipo di percorso',
        outcomes: 'Risultati',
        blockers: 'Blocchi',
        lessons: 'Apprendimenti',
        priority: 'Prossima priorità',
        evidence: 'Prova'
      },
      errors: {
        ai_not_configured: 'Orivon AI non è ancora configurata sul server.',
        provider_timeout: 'La risposta sta richiedendo troppo tempo. Riprova tra poco.',
        provider_rate_limited: 'Il servizio AI è temporaneamente molto occupato. Riprova tra poco.',
        provider_unavailable: 'Orivon AI è temporaneamente non disponibile. Riprova tra poco.',
        provider_refusal: 'Questa richiesta non può essere completata. Riformulala come domanda professionale.',
        provider_invalid_output: 'La risposta non ha superato i controlli di qualità. Riprova.',
        provider_auth_failed: 'La configurazione AI richiede un intervento tecnico.',
        ai_daily_limit: 'Hai raggiunto il limite giornaliero di Orivon AI. Potrai continuare domani.',
        ai_monthly_limit: 'Hai raggiunto il limite mensile di Orivon AI.',
        ai_request_failed: 'Non è stato possibile completare la richiesta AI.'
      },
      memoryBody: 'La memoria conserva decisioni e preferenze utili delle conversazioni. La controlli tu.',
      memoryActive: 'Usa la memoria in questa conversazione',
      memoryTypes: {
        fact: 'Fatto',
        decision: 'Decisione',
        preference: 'Preferenza',
        plan: 'Piano'
      },
      remove: 'Rimuovi',
      noMemory: 'Nessuna memoria salvata.',
      clearMemory: 'Cancella tutta la memoria',
      clearMemoryConfirm: 'Cancellare definitivamente tutta la memoria di Orivon AI?'
    },
    social: {
      feedTitle: 'Conversazioni professionali',
      feedBody: 'Condividi prove, decisioni e domande utili con membri reali di Orivon.',
      general: 'Feed generale',
      category: 'Feed della categoria',
      createPlaceholder: 'Condividi un’osservazione utile, una domanda o un progresso…',
      linkPlaceholder: 'Link facoltativo (https://…)',
      publish: 'Pubblica',
      publishing: 'Pubblicazione…',
      postLimit: 'Massimo 2.000 caratteri.',
      member: 'Membro privato',
      edited: 'Modificato',
      useful: 'Utile',
      comments: 'Commenti',
      save: 'Salva',
      saved: 'Salvato',
      more: 'Altre azioni',
      edit: 'Modifica',
      delete: 'Elimina',
      report: 'Segnala',
      reportReason: 'Perché vuoi segnalarlo?',
      reportSent: 'Segnalazione inviata per la revisione.',
      deleteConfirm: 'Eliminare questo contenuto?',
      editPrompt: 'Modifica il contenuto',
      commentPlaceholder: 'Scrivi una risposta ragionata…',
      replyPlaceholder: 'Scrivi una risposta…',
      reply: 'Rispondi',
      submitComment: 'Commenta',
      noPosts: 'Non ci sono ancora post.',
      noPostsBody: 'Avvia una conversazione professionale reale. Orivon non inventa attività della community.',
      loadError: 'Impossibile caricare il feed della community.',
      actionError: 'Impossibile completare questa azione nella community.',
      reasons: {
        spam: 'Spam',
        harassment: 'Molestie',
        impersonation: 'Impersonificazione',
        scam: 'Truffa',
        inappropriate: 'Contenuto inappropriato',
        privacy: 'Problema di privacy',
        other: 'Altro'
      }
    },
    network: {
      title: 'Costruisci una rete professionale utile.',
      subtitle: 'Orivon spiega perché una persona può essere rilevante senza presentare la compatibilità come certezza.',
      suggestions: 'Persone suggerite',
      connections: 'Connessioni',
      requests: 'Richieste',
      networkFeed: 'Feed delle connessioni',
      filters: 'Filtri di matching',
      allCategories: 'Tutte le categorie',
      sector: 'Settore',
      location: 'Città o paese',
      language: 'Lingua',
      apply: 'Applica filtri',
      clear: 'Azzera',
      connect: 'Connettiti',
      requestTitle: 'Invia una richiesta di connessione',
      requestTo: 'Presentati a {name}. Una nota breve e pertinente rende la richiesta più utile.',
      messageOptional: 'Presentazione facoltativa',
      messagePrompt: 'Aggiungi una presentazione pertinente facoltativa (massimo 500 caratteri).',
      characters: '{count}/500 caratteri',
      sendRequest: 'Invia richiesta',
      requestSent: 'Richiesta inviata',
      incoming: 'In arrivo',
      outgoing: 'Inviata',
      accept: 'Accetta',
      decline: 'Rifiuta',
      cancelRequest: 'Annulla richiesta',
      removeConnection: 'Rimuovi connessione',
      removeConfirm: 'Rimuovere questa connessione?',
      block: 'Blocca',
      blockConfirm: 'Bloccare questo membro? Connessioni e richieste in sospeso verranno rimosse.',
      emptySuggestions: 'Non ci sono ancora match spiegabili.',
      emptySuggestionsBody: 'Completa profilo e privacy. I suggerimenti compaiono solo quando membri reali e visibili condividono abbastanza dati pertinenti.',
      emptyConnections: 'Non ci sono ancora connessioni.',
      emptyRequests: 'Nessuna richiesta in sospeso.',
      reasonsTitle: 'Perché questo match',
      missingTitle: 'Contesto mancante',
      profile: 'Apri profilo',
      freeLimit: 'Free mostra fino a 5 suggerimenti spiegabili.',
      proLimit: 'Professional mostra fino a 20 suggerimenti spiegabili con filtri avanzati.',
      loadError: 'Impossibile caricare la tua rete.',
      actionError: 'Impossibile completare questa azione di rete.',
      privateMember: 'Membro privato',
      labels: {
        high: 'Segnale forte',
        medium: 'Segnale rilevante',
        emerging: 'Segnale iniziale'
      },
      types: {
        similar: 'Direzione simile',
        complementary: 'Complementare',
        more_advanced: 'Più avanti nel percorso',
        same_territory: 'Stesso territorio',
        relevant: 'Potenzialmente rilevante'
      },
      reasons: {
        shared_category: 'Stessa categoria Orivon',
        shared_sector: 'Stesso settore',
        same_city: 'Stessa città',
        shared_language: 'Lingua in comune',
        offers_what_you_seek: 'Offre qualcosa che stai cercando',
        seeks_what_you_offer: 'Cerca qualcosa che puoi offrire',
        further_on_path: 'Più avanti in un percorso correlato'
      }
    },
    messaging: {
      title: 'Conversazioni professionali private',
      subtitle: 'I messaggi sono disponibili solo tra connessioni accettate. Email e numero di telefono restano privati.',
      conversations: 'Conversazioni',
      privateMember: 'Connessione privata',
      noConversations: 'Non ci sono ancora conversazioni.',
      noConversationsBody: 'Connettiti con un membro prima di avviare una conversazione privata.',
      chooseConversation: 'Scegli una conversazione.',
      placeholder: 'Scrivi un messaggio…',
      send: 'Invia',
      deleted: 'Messaggio eliminato',
      delete: 'Elimina',
      deleteConfirm: 'Eliminare questo messaggio per entrambi i partecipanti?',
      report: 'Segnala',
      reportConfirm: 'Segnalare questo messaggio per contenuto inappropriato?',
      reportSent: 'Messaggio segnalato.',
      block: 'Blocca membro',
      blockConfirm: 'Bloccare questo membro e interrompere l’accesso ai messaggi?',
      loadError: 'Impossibile caricare i messaggi.',
      actionError: 'Impossibile completare questa azione sul messaggio.',
      unread: 'non letti'
    },
    notifications: {
      title: 'Notifiche',
      subtitle: 'Aggiornamenti su connessioni, community, messaggi e progressi generati da attività reali.',
      markAll: 'Segna tutto come letto',
      empty: 'Non ci sono ancora notifiche.',
      emptyBody: 'Gli aggiornamenti compariranno quando attività reali coinvolgeranno il tuo account.',
      loadError: 'Impossibile caricare le notifiche.',
      types: {
        connection_request: 'Nuova richiesta di connessione',
        connection_accepted: 'Connessione accettata',
        comment: 'Nuovo commento al tuo post',
        reply: 'Nuova risposta',
        reaction: 'Nuova reazione al tuo post',
        message: 'Nuovo messaggio privato',
        mission: 'Progresso missione aggiornato',
        milestone: 'Milestone completata',
        review: 'Revisione mensile aggiornata',
        ai_suggestion: 'Nuovo suggerimento di Orivon AI',
        opportunity: 'Nuova opportunità rilevante'
      }
    },
    opportunities: {
      title: 'Percorsi curati verso opportunità reali.',
      subtitle: 'Esplora ruoli, collaborazioni, programmi e strumenti selezionati da Orivon. La compatibilità viene spiegata, mai presentata come certezza.',
      curated: 'Selezione umana',
      type: 'Tipo di opportunità',
      allTypes: 'Tutti i tipi',
      sector: 'Settore',
      remoteOnly: 'Solo da remoto',
      savedOnly: 'Solo salvate',
      apply: 'Applica filtri',
      clear: 'Azzera',
      organic: 'Selezione Orivon',
      sponsored: 'Sponsorizzato',
      remote: 'Da remoto',
      deadline: 'Scadenza',
      why: 'Perché può essere rilevante',
      missing: 'Completa il profilo per avere più contesto',
      exploratoryBody: 'È un’opportunità esplorativa. Orivon non dispone ancora di dati sufficienti sul profilo per fornire una spiegazione più forte.',
      sponsoredDisclosure: 'Posizionamento a pagamento di {sponsor}. La sponsorizzazione non modifica mai l’analisi o la direzione professionale.',
      open: 'Apri opportunità',
      save: 'Salva',
      saved: 'Salvata',
      askAi: 'Valuta con Orivon AI',
      empty: 'Non ci sono ancora opportunità adatte pubblicate.',
      emptyBody: 'Orivon mostra solo opportunità reali e revisionate. Non inventa ruoli, programmi o attività per riempire questa pagina.',
      noSaved: 'Non hai ancora salvato opportunità.',
      noSavedBody: 'Salva un’opportunità rilevante per ritrovarla qui.',
      loadError: 'Impossibile caricare le opportunità.',
      actionError: 'Impossibile completare questa azione.',
      labels: {
        strong: 'Segnale forte',
        relevant: 'Segnale rilevante',
        exploratory: 'Esplorativa'
      },
      types: {
        role: 'Ruolo',
        collaboration: 'Collaborazione',
        program: 'Programma',
        event: 'Evento',
        course: 'Corso',
        accelerator: 'Acceleratore',
        tool: 'Strumento'
      },
      reasons: {
        category_alignment: 'Coerente con la tua categoria Orivon',
        sector_alignment: 'Coerente con il tuo settore',
        skill_alignment: 'Pertinente alle competenze del tuo profilo',
        remote_access: 'Accessibile da remoto',
        roadmap_stage_alignment: 'Compatibile con la fase attuale del percorso'
      }
    },
    admin: {
      staffWorkspace: 'Spazio staff',
      title: 'Modera con cura. Seleziona con criterio.',
      subtitle: 'Segnalazioni, pubblicazione delle opportunità e interventi vengono protetti e registrati in un audit non modificabile.',
      unauthorized: 'Accesso staff richiesto',
      unauthorizedBody: 'Questa sezione è disponibile solo ai moderatori e amministratori Orivon autorizzati.',
      openReports: 'Segnalazioni aperte',
      publishedOpportunities: 'Opportunità pubblicate',
      activeSuspensions: 'Sospensioni attive',
      tabs: {
        reports: 'Segnalazioni',
        opportunities: 'Opportunità',
        suspensions: 'Sospensioni',
        activity: 'Registro attività'
      },
      noDetails: 'Nessun dettaglio aggiuntivo.',
      noReports: 'Nessuna segnalazione da revisionare.',
      noOpportunities: 'Non sono ancora state curate opportunità.',
      noSuspensions: 'Nessuna sospensione attiva.',
      noActivity: 'Nessun intervento di moderazione.',
      moderationNote: 'Aggiungi una nota interna di moderazione.',
      actionDialogBody: 'Controlla l’elemento e conferma questo intervento staff registrato.',
      resolveReportTitle: 'Risolvi segnalazione',
      dismissReportTitle: 'Archivia segnalazione',
      hideContentTitle: 'Nascondi contenuto segnalato',
      suspendDialogTitle: 'Sospendi accesso alla community',
      unsuspendDialogTitle: 'Rimuovi sospensione dalla community',
      editCategoryTitle: 'Modifica categoria',
      confirmAction: 'Conferma intervento',
      hideContent: 'Nascondi contenuto',
      dismiss: 'Archivia',
      resolve: 'Risolvi',
      edit: 'Modifica',
      newOpportunity: 'Nuova opportunità',
      editOpportunity: 'Modifica opportunità',
      curation: 'Curatela opportunità',
      titleEn: 'Titolo inglese',
      titleIt: 'Titolo italiano',
      descriptionEn: 'Descrizione inglese',
      descriptionIt: 'Descrizione italiana',
      type: 'Tipo',
      sector: 'Settore',
      location: 'Località',
      source: 'Fonte',
      placement: 'Posizionamento',
      sponsor: 'Nome sponsor',
      categories: 'Categorie obiettivo (separate da virgole)',
      skills: 'Competenze obiettivo (separate da virgole)',
      deadline: 'Scadenza',
      status: 'Stato',
      remote: 'Da remoto',
      until: 'Fino al',
      indefinite: 'Senza scadenza',
      unsuspend: 'Rimuovi sospensione',
      unsuspendConfirm: 'Rimuovere questa sospensione dalla community?',
      unsuspendedByStaff: 'Sospensione rimossa dopo revisione dello staff.',
      suspensionReason: 'Perché sospendere questo membro dalle attività della community?',
      suspensionReasonRequired: 'Inserisci una motivazione di almeno tre caratteri.',
      suspendMember: 'Sospendi accesso alla community',
      memberId: 'ID utente del membro',
      suspend: 'Sospendi',
      enforcement: 'Interventi',
      taxonomy: 'Tassonomia',
      categoriesTitle: 'Categorie Orivon',
      categoryNameEn: 'Nome inglese della categoria',
      categoryNameIt: 'Nome italiano della categoria',
      categoryDescriptionEn: 'Descrizione inglese della categoria',
      categoryDescriptionIt: 'Descrizione italiana della categoria',
      saved: 'Modifica staff salvata e registrata.',
      actionError: 'Impossibile completare l’azione staff.'
    },
    modules: {
      roadmapTitle: 'Il tuo percorso professionale',
      roadmapBody: 'Visualizza livello attuale, direzione finale e progressi verificati.',
      aiTitle: 'Orivon AI',
      aiBody: 'Un assistente contestuale per profilo, percorso e decisioni.',
      communityTitle: 'Community',
      communityBody: 'Conversazioni professionali di categoria, rete e generali.',
      networkTitle: 'La tua rete',
      networkBody: 'Persone con obiettivi simili, competenze complementari o esperienza rilevante.',
      messagesTitle: 'Messaggi',
      messagesBody: 'Conversazioni private con connessioni autorizzate.',
      opportunitiesTitle: 'Opportunità',
      opportunitiesBody: 'Ruoli, collaborazioni, programmi e risorse curate.',
      notificationsTitle: 'Notifiche',
      notificationsBody: 'Aggiornamenti su connessioni, community, missioni e messaggi.',
      settingsTitle: 'Impostazioni',
      settingsBody: 'Account, profilo, privacy, lingua, notifiche e abbonamento.',
      foundation: 'La fondazione del modulo Launch V1 è attiva. I flussi dati vengono completati milestone per milestone su questo branch di anteprima.'
    }
  }
} as const;

export type Messages = (typeof messages)[Locale];
